import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import { Button, Typography, TextField, InputAdornment, IconButton, Popover, FormControlLabel, Checkbox } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useHistory } from "react-router-dom";
import Urls from "@egovernments/digit-ui-libraries/src/services/atoms/urls";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Toast } from "@egovernments/digit-ui-react-components";

const NOCTakeAction = ({ formData: propsFormData, onNocUpdate }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const authToken = Digit.UserService.getUser()?.access_token || null;
  console.log("authToken", authToken);

  const sessionData = propsFormData || Digit.SessionStorage.get("nocAppData");
  const [storedData, setStoredData] = useState(sessionData);
  const [uploadingRowId, setUploadingRowId] = useState(null);
  const [showToast, setShowToast] = useState(null);

  useEffect(() => {
    if (propsFormData) {
      // Merge: preserve any locally-uploaded NOC documents that may not yet be
      // reflected in the parent params (e.g. after a tab switch before re-fetch).
      setStoredData((prev) => {
        if (!prev) return propsFormData;
        // Build a merged nocList: start from the incoming props nocList then
        // overlay any document arrays that were set locally during this session.
        const incomingNocList =
          propsFormData?.nocList ||
          propsFormData?.nocDetails?.nocListApplicantDoc ||
          propsFormData?.nocDetails?.nocList ||
          [];
        const localNocList =
          prev?.nocList ||
          prev?.nocDetails?.nocListApplicantDoc ||
          prev?.nocDetails?.nocList ||
          [];

        // For each item in the incoming list, prefer the local documents if
        // the local item already has a fileStoreId (uploaded this session).
        const mergedNocList = incomingNocList.map((incoming) => {
          const localMatch = localNocList.find(
            (l) =>
              (l.id && l.id === incoming.id) ||
              (l.applicationNo && l.applicationNo === incoming.applicationNo)
          );
          if (!localMatch) return incoming;
          // Keep local documents if they contain an uploaded fileStoreId;
          // otherwise use what the API returned (which may have the file too).
          const localHasDoc = Array.isArray(localMatch?.documents) &&
            localMatch.documents.some((d) => d?.fileStoreId);
          const incomingHasDoc = Array.isArray(incoming?.documents) &&
            incoming.documents.some((d) => d?.fileStoreId);
          const bestDocuments = localHasDoc ? localMatch.documents :
            incomingHasDoc ? incoming.documents :
            incoming.documents;
          return { ...incoming, documents: bestDocuments };
        });

        return {
          ...propsFormData,
          nocList: mergedNocList,
          nocDetails: {
            ...(propsFormData?.nocDetails || {}),
            nocListApplicantDoc: mergedNocList,
          },
        };
      });
    }
  }, [propsFormData]);

  const nocList = storedData?.nocList || storedData?.nocDetails?.nocListApplicantDoc || storedData?.nocDetails?.nocList || [];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [globalSearch, setGlobalSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([
    "srNo",
    "applicationNo",
    "department", // <-- Ensure Department is selected by default
    "requestLetter",
    "shortfallLetter",
    "approvalLetter",
    "uploadNoc",
    "status",
    "action",
  ]);
  console.log("storedData", storedData);

  // Table columns with filterable property for future extensibility
  const allColumns = [
    { id: "srNo", label: t("FASTPAS.GLOBAL.SERIALNUMBER"), width: "5%", filterable: false },
    { id: "applicationNo", label: "Application Number", width: "16%", filterable: true },
    { id: "department", label: "Department", width: "12%", filterable: false }, // <-- Added Department column
    { id: "requestLetter", label: "Request Letter", width: "11%", filterable: false },
    { id: "shortfallLetter", label: "Shortfall/Rejection Letter", width: "11%", filterable: false },
    { id: "approvalLetter", label: "Approval Letter", width: "11%", filterable: false },
    { id: "uploadNoc", label: "Upload NOC", width: "15%", filterable: false },
    { id: "status", label: "Status", width: "10%", filterable: true },
    { id: "action", label: "Action", width: "9%", filterable: false },
  ];
  const userDetails = Digit.SessionStorage.get("USER_DETAIL");

  const columns = allColumns.filter((col) => selectedColumns.includes(col.id));
  const searchNocRemarks = async (applicationNumber) => {
    const dataToSend = {
      RequestInfo: {
        apiId: "Rainmaker",
        action: "_create",
        did: 1,
        key: "",
        msgId: "20170310130900|en_IN",
        ts: 0,
        ver: ".01",
        authToken: authToken,
      },
    };
    try {
      const response = await axios.post(`${Urls.bpaService.nocRemarks}&applicationNo=${applicationNumber}`, dataToSend);
      const respData = response.data;
      Digit.SessionStorage.set("nocTermsAndCondition", respData);
      console.log("ResultFrommsearch", respData);
      return respData;
    } catch (error) {
      console.log(error);
      // It's good practice to re-throw the error or return a specific error indicator
      // so the calling function knows the request failed.
      throw error;
    }
  };
  const handleEdit = async (item) => {
    console.log("itemFromHandleEdit", item);
    if (!item) {
      return;
    }
    await searchNocRemarks(item?.applicationNo);
    Digit.SessionStorage.set("nocAppNo", { nocAppNo: item?.applicationNo, nocSearchResult: item });
    history.push({
      pathname: `/digit-ui/citizen/obps/services/NOC/${item?.applicationNo}`,
    });
  };
  // Helper to open document in new tab
  const handleOpenDoc = (url) => {
    if (url) window.open(url, "_blank");
  };

  const handleOpenUploadedDoc = async (fileStoreId, tenantId) => {
    try {
      const res = await Digit.UploadServices.Filefetch([fileStoreId], tenantId);
      const url = res?.data?.[fileStoreId] || res?.data?.files?.[0]?.url;
      if (url) {
        window.open(url, "_blank");
      } else {
        setShowToast({ key: "error", error: true, label: "Document URL not available" });
        setTimeout(() => setShowToast(null), 5000);
      }
    } catch (err) {
      console.error("Error fetching file URL", err);
    }
  };

  const handleFileUpload = async (event, item) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const rowId = item.id || item.applicationNo;
    try {
      setUploadingRowId(rowId);
      const tenantId = item?.tenantId || storedData?.tenantId || Digit.ULBService.getStateId();

      // 1. Upload file via UploadServices
      const uploadRes = await Digit.UploadServices.Filestorage("property-upload", file, tenantId);
      const fileStoreId = uploadRes?.data?.files?.[0]?.fileStoreId;

      if (!fileStoreId) {
        throw new Error("File upload failed: No filestore ID received");
      }

      // 2. Construct document object
      const nocLetterObj = Array.isArray(item?.nocLetterInfo) ? item?.nocLetterInfo?.[0] : item?.nocLetterInfo;
      const nocDocName = item?.nocName || nocLetterObj?.nocName || item?.nocType || "NOC Document";

      // Determine the existing doc id — first check item.documents, then fall back
      // to the top-level BPA documents array (storedData.documents) which is where
      // citizen uploads land before the shortfall flow populates nocList[i].documents.
      let existingDocId = item?.documents?.[0]?.id || undefined;
      if (!existingDocId) {
        const bpaDocs = Array.isArray(storedData?.documents) ? storedData.documents : [];
        const fallbackDoc = bpaDocs.find(
          (doc) =>
            (doc?.isNoc === "true" || doc?.isNoc === true) &&
            doc?.documentType === nocDocName
        );
        existingDocId = fallbackDoc?.id || undefined;
      }

      const newDoc = {
        id: existingDocId,
        documentType: nocDocName,
        fileStoreId: fileStoreId,
        documentRemarks: "",
        documentSubtype: null,
        isNoc: "true",
      };

      let updatedDocuments = [];
      if (Array.isArray(item.documents) && item.documents.length > 0) {
        const existingIndex = item.documents.findIndex(
          (d) => d?.isNoc === "true" || d?.documentType === nocDocName
        );
        if (existingIndex >= 0) {
          updatedDocuments = [...item.documents];
          updatedDocuments[existingIndex] = {
            ...updatedDocuments[existingIndex],
            fileStoreId: fileStoreId,
          };
        } else {
          updatedDocuments = [...item.documents, newDoc];
        }
      } else {
        // nocList[i].documents was null/empty — seed it with the new document.
        // Also check if a matching doc already exists in the top-level BPA documents
        // array so we can carry over its id for proper update vs create semantics.
        updatedDocuments = [newDoc];
      }

      // 3. Update nocList inside storedData
      const currentNocList = storedData?.nocDetails?.nocListApplicantDoc || storedData?.nocList || [];
      const updatedNocList = currentNocList.map((nocItem) => {
        if (
          (nocItem.id && nocItem.id === item.id) ||
          (nocItem.applicationNo && nocItem.applicationNo === item.applicationNo)
        ) {
          return {
            ...nocItem,
            documents: updatedDocuments,
            workflow: {
              ...(nocItem?.workflow || {}),
              action: "UPLOAD_DOC",
            },
          };
        }
        return nocItem;
      });

      /* 
      // 4. Update top-level documents array of storedData (BPA object)
      const bpaNocDocObj = {
        documentType: nocDocName,
        fileStoreId: fileStoreId,
        isNoc: true,
        isMandatory: false,
        additionalDetails: {
          fileName: file?.name || "",
        },
      };

      let updatedBpaDocuments = [];
      const currentBpaDocs = Array.isArray(storedData?.documents) ? storedData.documents : [];
      const existingBpaDocIndex = currentBpaDocs.findIndex(
        (doc) => (doc?.isNoc === true || doc?.isNoc === "true") && doc?.documentType === nocDocName
      );

      if (existingBpaDocIndex >= 0) {
        updatedBpaDocuments = [...currentBpaDocs];
        updatedBpaDocuments[existingBpaDocIndex] = {
          ...updatedBpaDocuments[existingBpaDocIndex],
          fileStoreId: fileStoreId,
          additionalDetails: {
            ...(updatedBpaDocuments[existingBpaDocIndex]?.additionalDetails || {}),
            fileName: file?.name || updatedBpaDocuments[existingBpaDocIndex]?.additionalDetails?.fileName || "",
          },
        };
      } else {
        updatedBpaDocuments = [...currentBpaDocs, bpaNocDocObj];
      }
      */

      const updatedAppData = {
        ...storedData,
        nocList: updatedNocList,
        nocDetails: {
          ...(storedData?.nocDetails || {}),
          nocListApplicantDoc: updatedNocList,
        },
        // documents: updatedBpaDocuments,
        workflow: {
          ...(storedData?.workflow || {}),
          action: "UPLOAD_DOC",
        },
      };

      // 5. Call BPA Update API
      const updateResponse = await Digit.OBPSService.update({ BPA: updatedAppData }, tenantId);
      const finalUpdatedBPA = updateResponse?.BPA?.[0] || updatedAppData;

      // Build the best nocList from the API response, falling back to our
      // locally-constructed list so uploaded documents are always present.
      const apiNocList =
        finalUpdatedBPA?.nocList ||
        finalUpdatedBPA?.nocDetails?.nocListApplicantDoc ||
        updatedNocList;

      // Reconcile: for each item in the api list, overlay our locally-set
      // documents when the API stripped them (some backends drop pending docs).
      const reconciledNocList = apiNocList.map((apiItem) => {
        const localItem = updatedNocList.find(
          (l) =>
            (l.id && l.id === apiItem.id) ||
            (l.applicationNo && l.applicationNo === apiItem.applicationNo)
        );
        if (!localItem) return apiItem;
        const apiHasDoc = Array.isArray(apiItem?.documents) &&
          apiItem.documents.some((d) => d?.fileStoreId);
        const localHasDoc = Array.isArray(localItem?.documents) &&
          localItem.documents.some((d) => d?.fileStoreId);
        return {
          ...apiItem,
          documents: apiHasDoc ? apiItem.documents :
            localHasDoc ? localItem.documents :
            apiItem.documents,
        };
      });

      const finalData = {
        ...finalUpdatedBPA,
        nocList: reconciledNocList,
        nocDetails: {
          ...(finalUpdatedBPA?.nocDetails || {}),
          nocListApplicantDoc: reconciledNocList,
        },
      };

      // 6. Update session storage and local state
      Digit.SessionStorage.set("nocAppData", finalData);
      setStoredData(finalData);

      // 7. Also persist into the parent form session storage so tab switches
      //    do not erase the uploaded NOC documents from params.
      const parentFormKey = "FORM_MAP_SUBMISSION";
      const currentParentForm = Digit.SessionStorage.get(parentFormKey);
      if (currentParentForm) {
        Digit.SessionStorage.set(parentFormKey, {
          ...currentParentForm,
          nocList: reconciledNocList,
          nocDetails: {
            ...(currentParentForm?.nocDetails || {}),
            nocListApplicantDoc: reconciledNocList,
          },
        });
      }

      // 8. Notify parent portal to update its React state (params) immediately,
      //    so tab switches do not erase the uploaded NOC file from the table.
      if (typeof onNocUpdate === "function") {
        onNocUpdate(reconciledNocList);
      }

      setShowToast({ key: "success", label: "NOC document uploaded and application updated successfully!" });
      setTimeout(() => setShowToast(null), 5000);
    } catch (err) {
      console.error("Error uploading NOC document:", err);
      setShowToast({ key: "error", error: true, label: err?.message || "Failed to upload NOC document" });
      setTimeout(() => setShowToast(null), 5000);
    } finally {
      setUploadingRowId(null);
      if (event.target) event.target.value = "";
    }
  };

  // Filtering logic
  const matchesSearch = (value, search) => {
    if (!value) return false;
    return value.toString().toLowerCase().includes(search.toLowerCase());
  };

  const getFilteredData = () => {
    let filteredData = nocList;
    if (globalSearch) {
      filteredData = filteredData.filter((item) => matchesSearch(item?.applicationNo, globalSearch) || matchesSearch(item?.status, globalSearch));
    }
    return filteredData;
  };

  const filteredData = getFilteredData();
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  console.log("paginatedData", paginatedData);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOpenColumnFilter = (event) => setAnchorEl(event.currentTarget);
  const handleCloseColumnFilter = () => setAnchorEl(null);

  const handleColumnToggle = (columnId) => {
    if (selectedColumns.includes(columnId)) {
      if (selectedColumns.length > 1) {
        setSelectedColumns(selectedColumns.filter((id) => id !== columnId));
      }
    } else {
      setSelectedColumns([...selectedColumns, columnId]);
    }
  };

  return (
    <div style={{ padding: "0px 4px" }}>
      <TableContainer className="citizen-table-container">
        <div>
          <Typography variant="h5" gutterBottom>
            NOC Applications
          </Typography>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "20px" }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleOpenColumnFilter}
              size="small"
              sx={{
                height: "27px",
                fontSize: "12px",
                borderColor: "rgb(31, 74, 124)",
                color: "rgb(31, 74, 124)",
              }}
            >
              {t("FASTPAS.GLOBAL.FILTERS.TEXT")}({selectedColumns.length}/{allColumns.length})
            </Button>
            <TextField
              variant="outlined"
              size="small"
              placeholder={t('FASTPAS.GLOBAL.SEARCH.HERE.TEXT')}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ background: "#1f4a7c", color: "#fff", height: "24px", borderRadius: "7px 0px 0px 7px", width: "29px" }} />
                  </InputAdornment>
                ),
                endAdornment: globalSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setGlobalSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                padding: "0px",
                height: "25px",
                "& .MuiInputBase-root": {
                  height: "27px",
                  padding: "0px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgb(31, 74, 124)",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  fontSize: "12px",
                },
              }}
            />
          </div>
        </div>
        {/* Column selection popover */}
        <div>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseColumnFilter}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <div className="select-column-popover">
              <Typography variant="subtitle2" gutterBottom>
                Select Columns to Display
              </Typography>
              {allColumns.map((column) => (
                <div key={column.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{ padding: "0px" }}
                        checked={selectedColumns.includes(column.id)}
                        onChange={() => handleColumnToggle(column.id)}
                        disabled={column.id === "srNo" && selectedColumns.length === 1}
                      />
                    }
                    sx={{ margin: "0px" }}
                    label={column.label}
                  />
                </div>
              ))}
            </div>
          </Popover>
        </div>
        <Table stickyHeader aria-label="sticky table" className="citizen-application-table">
          <thead>
            <tr className="citizen-header-row">
              {columns.map((column) => (
                <td key={column.id} style={{ width: column.width, fontWeight: "bold" }}>
                  {column.label}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} align="center">
                  No records found
                </td>
              </tr>
            ) : (
              paginatedData.map((item, idx) => (
                <tr key={item.applicationNo || idx}>
                  {columns.map((column) => {
                    switch (column.id) {
                      case "srNo":
                        return <td key={column.id}>{page * rowsPerPage + idx + 1}</td>;
                      case "applicationNo":
                        return <td key={column.id}>{item.applicationNo || "-"}</td>;
                      case "department": {
                        let rawNocLetter = item?.nocLetterInfo;
                        if (typeof rawNocLetter === "string") {
                          try { rawNocLetter = JSON.parse(rawNocLetter); } catch (e) { }
                        }
                        const nocLetterObj = Array.isArray(rawNocLetter) ? rawNocLetter?.[0] : rawNocLetter;
                        return <td key={column.id}>{item.nocName || nocLetterObj?.nocName || "-"}</td>;
                      }
                      case "requestLetter": {
                        let rawNocLetter = item?.nocLetterInfo;
                        if (typeof rawNocLetter === "string") {
                          try { rawNocLetter = JSON.parse(rawNocLetter); } catch (e) { }
                        }
                        const nocLetter = Array.isArray(rawNocLetter)
                          ? rawNocLetter.find((info) => info?.fileStoreUrl || info?.fileStoreId || info?.url) || rawNocLetter?.[0]
                          : rawNocLetter;

                        const fileUrl = nocLetter?.fileStoreUrl || nocLetter?.url || item?.requestLetterUrl || item?.fileStoreUrl;
                        const fileStoreId = nocLetter?.fileStoreId || item?.requestLetterFileStoreId || item?.fileStoreId;

                        return (
                          <td key={column.id} style={{ textAlign: "center" }}>
                            {fileUrl ? (
                              <Button
                                size="small"
                                className="btn-table-action"
                                variant="contained"
                                color="primary"
                                sx={{ fontSize: "12px", minWidth: "60px" }}
                                onClick={() => handleOpenDoc(fileUrl)}
                              >
                                <span>View</span>
                              </Button>
                            ) : fileStoreId ? (
                              <Button
                                size="small"
                                className="btn-table-action"
                                variant="contained"
                                color="primary"
                                sx={{ fontSize: "12px", minWidth: "60px" }}
                                onClick={() =>
                                  handleOpenUploadedDoc(fileStoreId, item?.tenantId || storedData?.tenantId || Digit.ULBService.getStateId())
                                }
                              >
                                <span>View</span>
                              </Button>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      }
                      case "shortfallLetter":
                        return (
                          <td key={column.id} style={{ textAlign: "center" }}>
                            {item.nocApprovedLetterUrl &&
                              (item.applicationStatus === "REVERT_TO_APPLICANT_NOC" || item.applicationStatus === "NOC_REJECTED") ? (
                              <Button
                                size="small"
                                className="btn-table-action"
                                variant="contained"
                                color="primary"
                                sx={{ fontSize: "12px", minWidth: "60px" }}
                                onClick={() => handleOpenDoc(item.nocApprovedLetterUrl)}
                              >
                                <span>View</span>
                              </Button>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      case "approvalLetter":
                        return (
                          <td key={column.id} style={{ textAlign: "center" }}>
                            {item.applicationStatus === "NOC_APPROVED" ? (
                              <Button
                                size="small"
                                className="btn-table-action"
                                variant="contained"
                                color="primary"
                                sx={{ fontSize: "12px", minWidth: "60px" }}
                                onClick={() => handleOpenDoc(item.nocApprovedLetterUrl || item.nocDeemedLetterUrl)}
                              >
                                <span>View</span>
                              </Button>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      case "uploadNoc": {
                        const isUploading = uploadingRowId === (item.id || item.applicationNo);
                        const nocLetterObj = Array.isArray(item?.nocLetterInfo) ? item?.nocLetterInfo?.[0] : item?.nocLetterInfo;
                        const expectedDocName = item?.nocName || nocLetterObj?.nocName || item?.nocType;

                        // Search for the uploaded NOC document:
                        // Priority 1: item.documents (nocList[i].documents) — populated after
                        //   the citizen uploads via this component or if the API returns them.
                        // Priority 2: storedData.documents (top-level BPA documents array) —
                        //   this is where citizen-side uploads land before the shortfall flow
                        //   sets nocList[i].documents. Match by isNoc==="true" AND documentType.
                        let nocDoc = null;
                        if (Array.isArray(item?.documents) && item.documents.length > 0) {
                          nocDoc =
                            item.documents.find(
                              (doc) =>
                                doc?.isNoc === "true" ||
                                (expectedDocName && doc?.documentType === expectedDocName)
                            ) ||
                            item.documents.find((doc) => doc?.fileStoreId);
                        }

                        // Fallback: scan the top-level BPA documents array
                        if (!nocDoc && expectedDocName) {
                          const bpaDocs = Array.isArray(storedData?.documents) ? storedData.documents : [];
                          nocDoc =
                            bpaDocs.find(
                              (doc) =>
                                (doc?.isNoc === "true" || doc?.isNoc === true) &&
                                doc?.documentType === expectedDocName
                            ) ||
                            bpaDocs.find(
                              (doc) =>
                                (doc?.isNoc === "true" || doc?.isNoc === true) &&
                                doc?.fileStoreId
                            );
                        }

                        const uploadedFileStoreId = nocDoc?.fileStoreId;

                        return (
                          <td key={column.id} style={{ textAlign: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                              <input
                                type="file"
                                id={`upload-noc-${item.id || idx}`}
                                style={{ display: "none" }}
                                onChange={(e) => handleFileUpload(e, item)}
                                accept="image/*,application/pdf"
                              />
                              <label htmlFor={`upload-noc-${item.id || idx}`}>
                                <Button
                                  size="small"
                                  className="btn-table-action"
                                  variant="contained"
                                  color="primary"
                                  component="span"
                                  disabled={isUploading}
                                  sx={{ fontSize: "12px", minWidth: "75px" }}
                                >
                                  {isUploading ? "Uploading..." : uploadedFileStoreId ? "Re-upload" : "Upload"}
                                </Button>
                              </label>
                              {uploadedFileStoreId && (
                                <Button
                                  size="small"
                                  className="btn-table-action"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontSize: "12px", minWidth: "50px" }}
                                  onClick={() =>
                                    handleOpenUploadedDoc(uploadedFileStoreId, item?.tenantId || storedData?.tenantId || Digit.ULBService.getStateId())
                                  }
                                >
                                  <span>View</span>
                                </Button>
                              )}
                            </div>
                          </td>
                        );
                      }
                      case "status":
                        return <td key={column.id}>{t(`NOC_STATUS_${item.applicationStatus}`) || "-"}</td>;
                      case "action":
                        return (
                          <td className="td-action" style={{ textAlign: "center" }}>
                            {["REVERT_TO_APPLICANT_NOC"].includes(item?.applicationStatus) && (
                              <Button
                                size="small"
                                className="btn-table-action"
                                variant="contained"
                                color="primary"
                                sx={{ fontSize: "12px", minWidth: "60px" }}
                                onClick={() => {
                                  handleEdit(item);
                                  console.log("Edit action for application:", item.applicationNo);
                                }}
                              >
                                <span>Edit</span>
                              </Button>
                            )}
                          </td>
                        );
                      default:
                        return <td key={column.id}>-</td>;
                    }
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      {showToast && (
        <Toast
          error={showToast.error}
          label={t(showToast.label)}
          onClose={() => setShowToast(null)}
        />
      )}
    </div>
  );
};

export default NOCTakeAction;

